import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import Login from './Login';
import InvitationOnly from './InvitationOnly';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import AuthLayout from './AuthLayout';

const AuthGuard = ({ children }) => {
  const { user, loading, initialized, isAuthenticated } = useAuth();
  const [authView, setAuthView] = React.useState('login'); // 'login', 'signup', 'forgot-password', 'reset-password'
  const [resetToken, setResetToken] = React.useState(null);

  // Check for password reset token in URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setAuthView('reset-password');
      // Clean URL without token parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Local dev bypass — REACT_APP_DEV_AUTH_BYPASS=true in .env.local
  if (process.env.REACT_APP_DEV_AUTH_BYPASS === 'true') {
    return <>{children}</>;
  }

  // Show loading spinner while initializing auth
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-surface-alt py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-floral-600"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show login screen
  if (!isAuthenticated()) {
    return (
      <AuthLayout>
        {authView === 'signup' && (
          <InvitationOnly
            onSwitchToLogin={() => setAuthView('login')}
          />
        )}

        {authView === 'forgot-password' && (
          <ForgotPassword
            onBackToLogin={() => setAuthView('login')}
          />
        )}

        {authView === 'reset-password' && (
          <ResetPassword
            token={resetToken}
            onBackToLogin={() => {
              setAuthView('login');
              setResetToken(null);
            }}
            onSuccess={() => {
              setAuthView('login');
              setResetToken(null);
            }}
          />
        )}

        {authView === 'login' && (
          <Login
            onSwitchToSignup={() => setAuthView('signup')}
            onSwitchToForgotPassword={() => setAuthView('forgot-password')}
            onSuccess={() => {
              // Login success is handled by AuthContext
            }}
          />
        )}
      </AuthLayout>
    );
  }

  // User is authenticated - show the protected app
  return <>{children}</>;
};

export default AuthGuard;